// The Subs tab (docs/job-view-brief.md sections 4 and 5; drawn as round 4, frame f4b; the column per build from the
// small screens brief, docs/small-screens-brief.md rows SB1 to SB5, Spencer 2026-10-24). Answers: which parts of this
// job are someone else's, what will they cost under each build, and at what markup? Sub markup left the Price strip for
// here: the company's beside this job's, gold when they differ, and a markup typed on any one line over the job's
// (Spencer, 2026-10-22). A sub's number is typed by a person and never worked out by the engine; the internal estimate is
// flagged as an estimate until a quote replaces it (the line's state: the status, the estimate, the quote, which one the
// price uses, kept as they were). The line's own number is the Base column; a build's cell inherits it (grey) until a
// cost is typed for that build (gold), or reads Left out where the engine finds nothing goes to the vendor under that
// build, or where it was left out by hand on the cell (sub_line.build_overrides). Each cell shows the cost with the
// marked-up price small beneath it, so the Markup column stays one per line. A sub carries its markup and no margin,
// and no sales tax. Below the width the columns need, the build cells drop to labelled lines under the sub's name.
// A sub is a vendor (Spencer, 2026-10-25): the line names one from the Vendors list through the one search picker, the
// vendors that do sub work first; and Applies to says which builds the line is in, one tap per build, writing the same
// left-out cell the build columns read (sub_line.build_overrides), so the fact has one home.

import { useMemo, useRef, useState, type ReactNode } from 'react';
import type { Resolution, SubLineBuildCell } from '@zerogap/engine';
import { deleteRow, insertRow, updateRow, useRows, type Row } from '../../lib/api';
import { daysSince, money, shortDate, toCents } from '../../lib/format';
import { baseBuild, pricedUnder } from '../../lib/job';
import { useSession } from '../../lib/session';
import { Btn, Edit, Info, Money } from '../../ui/kit';
import { Pick, type PickOption } from '../../ui/pick';
import { Wide } from '../../ui/wide';
import { useFits } from '../steps/StepTable';
import { uploadFile, useSignedUrl } from './Files';
import { PctBox } from './Price';
import type { JobCtx } from './Workspace';
import '../../styles/jobview-price.css';

const amount = (t: string) => (t.trim() === '' ? '' : /^\$?[\d,]*\.?\d+$/.test(t.trim()) ? t.replace(/[$,]/g, '') : undefined);
const blank = (v: unknown) => v === undefined || v === null || String(v).trim() === '';
const pctText = (v: unknown) => (blank(v) ? '' : String(Number(v)));

function QuoteFile({ line, jobId, canEdit }: { line: Row; jobId: string; canEdit: boolean }) {
  const { user } = useSession();
  const file = useRows('file', { eq: { id: line.quote_file_id }, enabled: !!line.quote_file_id }).rows[0];
  const url = useSignedUrl(file?.storage_path);
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      {file && (
        <a href={url ?? undefined} target="_blank" rel="noreferrer">
          {file.filename.split('.').pop()?.toUpperCase() ?? 'file'}
        </a>
      )}
      {canEdit && (
        <>
          <input
            ref={input}
            type="file"
            className="hide"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              const row = await uploadFile(f, 'job', jobId, user?.id ?? null);
              await updateRow('sub_line', line.id, { quote_file_id: row.id });
            }}
          />{' '}
          <button type="button" className="tag off" onClick={() => input.current?.click()}>
            {file ? 'replace quote' : 'attach quote'}
          </button>
        </>
      )}
    </>
  );
}

export function Subs({ ctx }: { ctx: JobCtx }) {
  const { data, jobId, asEntered } = ctx;
  const s = useSession();
  const vendors = useRows('vendor', { order: ['name'] });
  // the subs first, then every other vendor; no vendor is a pick too
  const vendorOptions: PickOption[] = useMemo(
    () => [
      { id: '', label: 'No vendor picked' },
      ...vendors.rows.filter((v) => v.does_sub_work).map((v) => ({ id: v.id as string, label: String(v.name), group: 'Subs' })),
      ...vendors.rows.filter((v) => !v.does_sub_work).map((v) => ({ id: v.id as string, label: String(v.name), group: 'Other vendors' })),
    ],
    [vendors.rows],
  );
  const statuses = s.setting<string[]>('sub_line_statuses', []);
  const canEdit = s.canEdit('screen.subs') && !ctx.locked;
  const res = ctx.r.res!;
  const lib = asEntered.lib!;
  const save = (id: string, patch: Row) => updateRow('sub_line', id, patch);
  const waitingWords = /wait|reached/i;
  // the job's sub markup as typed, before it is saved: every line's price follows it live
  const [draft, setDraft] = useState<string | null>(null);
  const [lineDraft, setLineDraft] = useState<Record<string, string>>({});
  // the build cell being given its own cost: "<line id>:<build id>"
  const [editing, setEditing] = useState<string | null>(null);

  const company = (lib.pricing.sub_markup_pct ?? 0) as number | string;
  const jobOv = (data.job?.pricing_overrides ?? {}) as Record<string, number | string>;
  const jobMarkup = draft !== null ? (blank(draft) ? pctText(company) : draft) : pctText(blank(jobOv.sub_markup_pct) ? company : jobOv.sub_markup_pct);
  const jobDiffers = Number(jobMarkup) !== Number(company);
  const saveJob = async (t: string) => {
    const next = { ...jobOv };
    if (blank(t) || Number(t) === Number(company)) delete next.sub_markup_pct;
    else next.sub_markup_pct = Number(t);
    await updateRow('job', jobId, { pricing_overrides: next });
  };

  // the builds from the Scope table: the base first, then the others in their order
  const base = baseBuild(data.builds);
  const others = data.builds.filter((b) => b.id !== base?.id);
  // the job priced under each build, and where each line is left out there: by the item's rule (nothing goes to the
  // vendor, 2026-10-17) or by hand on the cell (2026-10-24)
  const { byBuild, leftOut } = useMemo(() => {
    const byBuild = new Map<string, Resolution | null>();
    const leftOut = new Map<string, { build: Row; rule: string; by_hand: boolean }[]>();
    for (const b of data.builds) {
      const r = pricedUnder(asEntered, b, []);
      byBuild.set(b.id as string, r);
      for (const o of r?.sub_lines_off ?? []) leftOut.set(o.sub_line_id, [...(leftOut.get(o.sub_line_id) ?? []), { build: b, rule: o.rule, by_hand: !!o.by_hand }]);
    }
    return { byBuild, leftOut };
  }, [asEntered, data.builds]);

  // a number typed once on an item and marked sub work (CustomOne's number on Outside finishing) is a sub of that item:
  // it is listed here from its one place, never typed a second time (Spencer, 2026-10-15)
  const fromItems = res.groups.flatMap((g) =>
    g.bom
      .filter((b) => b.sub_work && Number(b.qty) > 0)
      .flatMap((b) => Object.entries(b.shares).filter(([, sh]) => sh.cents > 0).map(([itemId, sh]) => ({ key: `${b.key}|${itemId}`, line: b, item: res.items.find((i) => i.id === itemId), cents: sh.cents }))),
  );
  /** The same item line's sub work under another build, from that build's own pricing. */
  const itemWorkUnder = (lineKey: string, itemId: string, b: Row): number | null => {
    const r = byBuild.get(b.id as string);
    if (!r) return null;
    for (const g of r.groups) for (const bl of g.bom) if (bl.key === lineKey && bl.sub_work) return bl.shares[itemId]?.cents ?? 0;
    return 0;
  };
  const itemRow = (id: string | null | undefined) => (id ? data.items.find((i) => i.id === id) : undefined);
  const addOnName = (id: string | null | undefined) => (id ? (data.addOns.find((a) => a.id === id)?.name as string | undefined) : undefined);
  const resolvedItem = (id: string | null | undefined) => (id ? asEntered.res?.items.find((i) => i.id === id) : undefined);
  const priceOf = (cents: number, markup: string) => Math.round(cents * (1 + Number(markup || 0) / 100));

  // below the width the columns need, the build cells drop to labelled lines under the sub's name (as the steps table does)
  const need = 200 + 190 + 180 + 170 + 150 * others.length + 120 + 64;
  const { ref, fits } = useFits<HTMLDivElement>(need);
  const perBuild = others.length > 0;
  const baseHead = base ? `Base · ${base.name}` : 'Cost';

  /** A sub line's cell under one of the other builds: inherited (grey), typed for the build (gold), or Left out. */
  const buildCell = (l: Row, b: Row, cost: number, lineMarkup: string): ReactNode => {
    const ov = (l.build_overrides ?? {}) as Record<string, SubLineBuildCell>;
    const cell = ov[b.id as string];
    const key = `${l.id}:${b.id}`;
    const engineOff = (leftOut.get(l.id) ?? []).find((x) => x.build.id === b.id && !x.by_hand);
    const put = (next: SubLineBuildCell | undefined) => {
      const o = { ...ov };
      if (next === undefined) delete o[b.id as string];
      else o[b.id as string] = next;
      setEditing(null);
      return save(l.id, { build_overrides: o });
    };
    const back = canEdit && (
      <button type="button" className="x" title={`Back to inherited (${money(cost)})`} onClick={() => void put(undefined)}>
        ×
      </button>
    );
    if (cell?.left_out)
      return (
        <div className="bcell">
          <span className="row mid" style={{ gap: 4, justifyContent: 'flex-end' }}>
            <span className="tag fix" title="Left out of this build's price by hand: not in this price.">
              Left out
            </span>
            {back}
          </span>
          <small>by hand</small>
        </div>
      );
    if (engineOff)
      return (
        <div className="bcell">
          <span className="tag fix" title={`Its item sends the vendor nothing under this build (${engineOff.rule}), so the estimate is left out there.`}>
            Left out
          </span>
          <small>nothing to the vendor under this build</small>
        </div>
      );
    const typed = cell && !blank(cell.amount);
    if (typed || editing === key) {
      const value = typed ? String(cell!.amount) : '';
      const cents = typed ? toCents(cell!.amount) : cost;
      return (
        <div className="bcell">
          <span className="row mid" style={{ gap: 4, justifyContent: 'flex-end' }}>
            <span className={`in num${typed ? ' ov' : ''}`}>
              <Edit
                className="cell short right"
                disabled={!canEdit}
                value={value}
                placeholder={money(cost)}
                parse={amount}
                onSave={(v) => (v === '' || toCents(v) === cost ? put(undefined) : put({ amount: v }))}
              />
            </span>
            {canEdit && !typed && (
              <button type="button" className="tag off" title="Nothing goes to the vendor under this build: leave the line out of its price" onClick={() => void put({ left_out: true })}>
                leave out
              </button>
            )}
            {typed ? back : canEdit && (
              <button type="button" className="x" title="Keep inherited" onClick={() => setEditing(null)}>
                ×
              </button>
            )}
          </span>
          <small>price {money(priceOf(cents, lineMarkup))}</small>
        </div>
      );
    }
    return (
      <div className="bcell inh">
        {canEdit ? (
          <button type="button" className="inh" title={`Inherited: ${b.name} takes the line's own number. Click to type a cost for this build, or leave the line out of it.`} onClick={() => setEditing(key)}>
            {money(cost)}
          </button>
        ) : (
          <span title="Inherited from the line's own number">{money(cost)}</span>
        )}
        <small>price {money(priceOf(cost, lineMarkup))}</small>
      </div>
    );
  };

  /** Applies to: one tap per build puts the line in that build's price or leaves it out (a cost typed for the build is kept). */
  const appliesTo = (l: Row): ReactNode => {
    if (data.builds.length < 2) return null;
    const ov = (l.build_overrides ?? {}) as Record<string, SubLineBuildCell>;
    const toggle = (b: Row) => {
      const o = { ...ov };
      const cell: SubLineBuildCell = { ...(o[b.id as string] ?? {}) };
      if (cell.left_out) delete cell.left_out;
      else cell.left_out = true;
      if (Object.keys(cell).length) o[b.id as string] = cell;
      else delete o[b.id as string];
      return save(l.id, { build_overrides: o });
    };
    return (
      <span className="applies" role="group" aria-label={`Builds ${l.scope_text} applies to`}>
        <small>Applies to</small>
        {[base, ...others]
          .filter((b): b is Row => !!b)
          .map((b) => {
            const on = !ov[b.id as string]?.left_out;
            return (
              <button key={b.id} type="button" className={`tag${on ? ' on' : ' off'}`} aria-pressed={on} disabled={!canEdit} title={on ? `In ${b.name}'s price. Tap to leave it out of that build.` : `Left out of ${b.name}'s price. Tap to put it back in.`} onClick={() => void toggle(b)}>
                {b.name}
              </button>
            );
          })}
      </span>
    );
  };

  let atCost = 0;
  let inPrice = 0;
  const cols = 6 + (fits ? others.length : 0);

  return (
    <div className="jvs">
      <div className="strip">
        <div className="f">
          <span className="lbl">Sub markup, this job</span>
          <div className="row">
            <PctBox key={`sub-${pctText(jobOv.sub_markup_pct)}`} label="Sub markup, this job" value={jobMarkup} ov={jobDiffers} disabled={!canEdit} onDraft={setDraft} onSave={saveJob} />
          </div>
          <span className="std">
            company <b>{pctText(company)} %</b>
            {jobDiffers ? ' · this job differs' : ''}
          </span>
        </div>
        <span className="sp" />
        {canEdit && (
          <Btn small onClick={() => void insertRow('sub_line', { job_id: jobId, scope_text: 'New sub scope', status: statuses[0] ?? null, status_at: new Date().toISOString(), sort: data.subLines.length + 1 })}>
            + Sub line
          </Btn>
        )}
      </div>
      <div className="subs" ref={ref}>
        {data.subLines.length + fromItems.length === 0 ? (
          <p className="only">Nothing on this job is subcontracted yet.</p>
        ) : (
          <Wide className="sbwrap">
            <table className={`sb${perBuild ? ' per' : ''}`} aria-label="Sub lines">
              <thead>
                <tr>
                  <th>Sub line</th>
                  <th>Vendor</th>
                  <th>On</th>
                  <th className="r">{baseHead}</th>
                  {fits && others.map((b) => <th key={b.id} className="r">{b.name}</th>)}
                  <th className="r">Markup</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fromItems.map((f) => {
                  atCost += f.cents;
                  inPrice += priceOf(f.cents, jobMarkup);
                  const under = (b: Row): ReactNode => {
                    const c = f.item ? itemWorkUnder(f.line.key, f.item.id, b) : null;
                    return c === null ? (
                      <span className="only">—</span>
                    ) : (
                      <div className="bcell inh">
                        <span title="From the number typed on the item, under this build">{money(c)}</span>
                        <small>price {money(priceOf(c, jobMarkup))}</small>
                      </div>
                    );
                  };
                  return (
                    <tr key={f.key}>
                      <td style={{ minWidth: 200 }}>
                        {f.line.description} <span className="tag off">sub work</span>
                        <small>typed once, on {f.item?.number ?? 'its item'}; it is a sub from there</small>
                        {!fits && perBuild && (
                          <span className="bvals">
                            {others.map((b) => (
                              <span key={b.id} className="bv">
                                <span className="k">{b.name}</span>
                                {under(b)}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="mut">—</td>
                      <td className="mut">
                        {f.item ? (
                          <button type="button" className="linklike read" onClick={() => ctx.go({ tab: 'item', item: f.item!.id })}>
                            {f.item.number} · {f.item.name || f.item.catalog_item_name}
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="r">
                        <div className="bcell">
                          <Money cents={f.cents} />
                          <small>price {money(priceOf(f.cents, jobMarkup))}</small>
                        </div>
                      </td>
                      {fits && others.map((b) => <td key={b.id} className="r">{under(b)}</td>)}
                      <td className="r">
                        {jobMarkup} % <span className="std">job</span>
                      </td>
                      <td>
                        <Info left>Typed on the item's own line and marked sub work, so it takes this job's sub markup and no margin. Change it on {f.item?.number ?? 'the item'}.</Info>
                      </td>
                    </tr>
                  );
                })}
                {data.subLines.map((l) => {
                  const waited = daysSince(l.status_at);
                  const hasQuote = !blank(l.quote_amount);
                  const usingQuote = !!l.use_quote && hasQuote;
                  const cost = toCents(usingQuote ? l.quote_amount : l.internal_estimate);
                  const typedDraft = lineDraft[l.id];
                  const lineMarkup = typedDraft !== undefined ? (blank(typedDraft) ? jobMarkup : typedDraft) : blank(l.markup_pct) ? jobMarkup : pctText(l.markup_pct);
                  const own = typedDraft !== undefined ? !blank(typedDraft) : !blank(l.markup_pct);
                  const price = priceOf(cost, lineMarkup);
                  const off = leftOut.get(l.id) ?? [];
                  const item = itemRow(l.job_item_id);
                  const ri = resolvedItem(l.job_item_id);
                  const inAddOn = addOnName(item?.add_on_id);
                  // the base's own cell: left out there by the item's rule or by hand
                  const baseOff = base ? off.find((x) => x.build.id === base.id) : undefined;
                  atCost += cost;
                  inPrice += price;
                  return (
                    <tr key={l.id}>
                      <td style={{ minWidth: 200 }}>
                        <Edit className="cell" disabled={!canEdit} value={l.scope_text} onSave={(v) => save(l.id, { scope_text: v || 'Sub scope' })} />
                        <Edit className="cell" disabled={!canEdit} value={l.note ?? ''} placeholder="note" onSave={(v) => save(l.id, { note: v || null })} />
                        {!fits && perBuild && (
                          <span className="bvals">
                            {others.map((b) => (
                              <span key={b.id} className="bv">
                                <span className="k">{b.name}</span>
                                {buildCell(l, b, cost, lineMarkup)}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                      <td>
                        <Pick value={l.vendor_id ?? ''} options={vendorOptions} disabled={!canEdit} placeholder="No vendor picked" title="The Vendors list, the subs first" onPick={(id) => void save(l.id, { vendor_id: id || null })} />
                        <div className="row mid" style={{ gap: 6, marginTop: 3 }}>
                          <select aria-label={`Status of ${l.scope_text}`} disabled={!canEdit} value={l.status ?? ''} onChange={(e) => void save(l.id, { status: e.target.value || null, status_at: new Date().toISOString() })}>
                            <option value="">No status</option>
                            {statuses.map((x) => (
                              <option key={x} value={x}>
                                {x}
                              </option>
                            ))}
                          </select>
                        </div>
                        {l.status_at && (
                          <small className={waitingWords.test(l.status ?? '') && (waited ?? 0) >= 5 ? 'warn' : ''}>
                            since {shortDate(l.status_at)}
                            {waitingWords.test(l.status ?? '') && waited !== null ? ` · ${waited} d` : ''}
                          </small>
                        )}
                      </td>
                      <td className="mut">
                        <select className="on" aria-label={`What ${l.scope_text} belongs to`} disabled={!canEdit} value={l.job_item_id ?? ''} onChange={(e) => void save(l.id, { job_item_id: e.target.value || null })} title="A sub line that belongs to an item (an outside finishing line) rides on that item's price.">
                          <option value="">The job as a whole</option>
                          {/* every top-level item on the job, an add-on's items included */}
                          {data.items
                            .filter((i) => !i.parent_item_id)
                            .map((i) => (
                              <option key={i.id} value={i.id}>
                                {i.number} · {i.name}
                                {i.add_on_id ? ` · ${addOnName(i.add_on_id) ?? 'add-on'}` : ''}
                              </option>
                            ))}
                        </select>
                        {inAddOn && <small>{inAddOn} add-on: priced only with it on</small>}
                        {appliesTo(l)}
                        {!perBuild &&
                          off.map((x) => (
                            <span key={x.build.id} className="tag fix" title={x.by_hand ? 'Left out of this build by hand.' : 'Its item sends the vendor nothing under this build, so the estimate is left out there.'}>
                              left out under {x.build.name}
                            </span>
                          ))}
                      </td>
                      <td className="r">
                        <div className="cost">
                          <span>
                            <Money cents={cost} /> {!usingQuote && <span className="only">estimate</span>}
                            {ri && ri.unverified.any && (
                              <span className="tag unv" title={`${ri.number} is unverified`}>
                                unverified
                              </span>
                            )}
                          </span>
                          <span className="two">
                            <small>est.</small>
                            <Edit className="cell short right" disabled={!canEdit} value={l.internal_estimate ?? ''} parse={amount} onSave={(v) => save(l.id, { internal_estimate: v === '' ? null : v })} />
                            <small>quote</small>
                            <Edit className="cell short right" disabled={!canEdit} value={l.quote_amount ?? ''} placeholder="none yet" parse={amount} onSave={(v) => save(l.id, { quote_amount: v === '' ? null : v, ...(v === '' ? { use_quote: false } : {}) })} />
                          </span>
                          <span className="two">
                            <QuoteFile line={l} jobId={jobId} canEdit={canEdit} />
                            <button type="button" className={`tag${usingQuote ? ' on' : ''}`} disabled={!canEdit || !hasQuote} title={hasQuote ? 'One tap sets which number the price uses' : 'No quote yet'} onClick={() => void save(l.id, { use_quote: !l.use_quote })}>
                              uses {usingQuote ? 'the quote' : 'the estimate'}
                            </button>
                          </span>
                          {baseOff ? (
                            <span className="tag fix" title={baseOff.by_hand ? 'Left out of the base by hand.' : `Its item sends the vendor nothing under ${baseOff.build.name} (${baseOff.rule}), so the estimate is left out there.`}>
                              Left out under the base
                            </span>
                          ) : (
                            <small>price {money(price)}</small>
                          )}
                        </div>
                      </td>
                      {fits && others.map((b) => <td key={b.id} className="r">{buildCell(l, b, cost, lineMarkup)}</td>)}
                      <td className="r">
                        <span className="mi">
                          <PctBox
                            key={`m-${l.id}-${pctText(l.markup_pct)}`}
                            label={`Markup, ${l.scope_text}`}
                            value={lineMarkup}
                            ov={own}
                            disabled={!canEdit}
                            onDraft={(v) =>
                              setLineDraft((p) => {
                                const n = { ...p };
                                if (v === null) delete n[l.id];
                                else n[l.id] = v;
                                return n;
                              })
                            }
                            onSave={(t) => save(l.id, { markup_pct: blank(t) || Number(t) === Number(jobMarkup) ? null : Number(t) })}
                          />
                        </span>{' '}
                        {!own && <span className="std">job</span>}
                      </td>
                      <td>
                        <span className="row mid" style={{ gap: 4, flexWrap: 'nowrap' }}>
                          <Info left>
                            {own ? `Markup ${lineMarkup} % typed on this line, over the job's ${jobMarkup} %. Clear the box to follow the job again.` : `Markup follows this job's sub markup, ${jobMarkup} %.`} Cost {money(cost)} ({usingQuote ? 'the quote' : 'the internal estimate'}) × (1 + markup) = {money(price)}, no margin, no sales tax.
                            {l.status ? ` Status: ${l.status}${l.status_at ? ` since ${shortDate(l.status_at)}` : ''}.` : ''}
                            {perBuild ? ' A build\'s cell inherits this number (grey) until a cost is typed for it (gold); Left out means nothing goes to the vendor under that build, by the item\'s rule or by hand.' : ''}
                          </Info>
                          {canEdit && (
                            <Btn small title="Remove this sub line" onClick={() => window.confirm(`Remove "${l.scope_text}"?`) && void deleteRow('sub_line', l.id)}>
                              ×
                            </Btn>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={cols} className="only">
                    Every line above at cost <Money cents={atCost} /> · priced <Money cents={inPrice} /> · in {ctx.build ? `${ctx.build.name}'s` : 'the'} price: <Money cents={res.totals.subs_price_cents} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </Wide>
        )}
      </div>
    </div>
  );
}
